import type { DashboardStats } from 'types';
import type { MetricCardTrend } from '@/components/MetricCard';
import { formatNumber, formatMinorUnitsCompact, percentChange } from 'helpers/formatters';

/**
 * Turns one dashboard_stats row into the tiles that render from it.
 *
 * Pure and separated from the components so the labelling can be tested. Every
 * defect this replaced was a labelling defect rather than a rendering one: a
 * lifetime cumulative count described as "currently on site", an all-time
 * session total described as "over selected period", and öre printed as SEK.
 */

export type Metric = {
  title: string;
  value: string;
  description: string;
  subtitle: string;
  /** Absent when there is nothing honest to compare against. */
  trend?: MetricCardTrend;
};

const trendFor = (current: number, previous: number | null | undefined): MetricCardTrend | undefined => {
  const percent = percentChange(current, previous);
  if (percent === null) return undefined;
  return { percent, direction: percent >= 0 ? 'up' : 'down' };
};

/**
 * Share of recent sessions that entered a running experiment.
 *
 * Null when nothing was measured, so the tile can say so instead of claiming a
 * confident zero. The ratio can legitimately exceed 100%: a visitor in two
 * concurrent experiments is counted once by each, and the aggregates carry no
 * visitor identity to deduplicate on. That is surfaced in the tile copy rather
 * than clamped away, because it is itself the signal that tests overlap.
 */
export function trafficUnderTest(experimentSessions: number, recentSessions: number): number | null {
  if (!Number.isFinite(experimentSessions) || !Number.isFinite(recentSessions)) return null;
  if (recentSessions <= 0) return null;
  return (experimentSessions / recentSessions) * 100;
}

/**
 * The point-in-time card: what is true about the website right now. None of
 * these vary with the selected timespan, so none of them claim to.
 */
export type StatusInput = Pick<
  DashboardStats,
  'experimentSessions' | 'recentSessions' | 'runningCount' | 'draftCount' | 'deployedCount'
>;

export function buildStatusMetrics(stats: StatusInput): Metric[] {
  const share = trafficUnderTest(stats.experimentSessions ?? 0, stats.recentSessions ?? 0);

  return [
    {
      title: 'Traffic under test',
      value: share === null ? '—' : `${share.toFixed(1)}%`,
      description: share === null ? 'No traffic recorded' : 'Sessions entering a running experiment',
      subtitle:
        share === null
          ? 'Last 7 days'
          : 'Last 7 days. Overlapping experiments each count the same visitor, so this can exceed 100%.',
    },
    {
      title: 'Running Experiments',
      value: String(stats.runningCount ?? 0),
      description: 'Active experiments',
      subtitle: 'Currently collecting data',
    },
    {
      title: 'Drafts',
      value: String(stats.draftCount ?? 0),
      description: 'Pending experiments',
      subtitle: 'Ready to be launched',
    },
    {
      title: 'Deployed',
      value: String(stats.deployedCount ?? 0),
      description: 'Rolled out to all visitors',
      subtitle: 'Serving a winning variant',
    },
  ];
}

/**
 * The windowed card. Every figure here honours the selected timespan, and every
 * one of them carries a real period-over-period delta except under 'alltime',
 * which has no preceding window to compare against.
 */
export function buildWindowMetrics(stats: DashboardStats, conversionLabel: string): Metric[] {
  const { sessions, conversions, revenue, currencyCode } = stats;

  return [
    {
      title: 'Sessions',
      value: formatNumber(sessions ?? 0),
      description: 'Total sessions',
      subtitle: 'Visitors over selected period',
      trend: trendFor(sessions ?? 0, stats.previousSessions),
    },
    {
      title: 'Total Experiments',
      value: String(stats.totalCount ?? 0),
      description: 'All experiments',
      // Point-in-time, sitting on a windowed card. It used to read "Created in
      // this period", which it never was.
      subtitle: 'Not archived or deleted, all time',
    },
    {
      title: conversionLabel,
      value: formatNumber(conversions ?? 0),
      description: 'Tracked conversion events',
      subtitle: 'Across all experiments',
      trend: trendFor(conversions ?? 0, stats.previousConversions),
    },
    {
      title: 'Total Value',
      value: formatMinorUnitsCompact(revenue ?? 0, currencyCode ?? null),
      description: 'Revenue generated',
      subtitle: currencyCode ? `${currencyCode} only, over selected period` : 'Over selected period',
      trend: trendFor(revenue ?? 0, stats.previousRevenue),
    },
  ];
}

/**
 * What the conversions tile is called.
 *
 * The number counts the website's configured conversion event, so naming it
 * after that event is the difference between a tile a lead-gen customer can
 * read and one that says "conversions" while counting signups.
 */
export const conversionMetricLabel = (conversionEvent: string | null | undefined): string => {
  if (!conversionEvent) return 'Total Conversions';
  return `Total ${conversionEvent.charAt(0).toUpperCase()}${conversionEvent.slice(1)}`;
};
